import { useState, useEffect, useCallback, useRef } from "react";
import { useAtom } from "klyva";
import * as t from "io-ts";
import { authStore, login, logout } from "./stores/auth-store";
import fetchApi from "./fetch-api";

export type RoleT =
  | "FREE"
  | "ANALYST"
  | "PREMIUM"
  | "PREMIUM_PLUS"
  | "ADMIN"
  | "ADMIN_MANAGER"
  | "SUPER_ADMIN";

interface SessionData {
  token: string;
  refreshToken?: string;
  userId?: string;
  name?: string;
  username?: string;
  email?: string;
  isAuthenticated: boolean;
  expiresAt?: number;
  role?: RoleT;
  environment?: string;
  rememberMe?: boolean;
}

interface UseSession {
  isAuthenticated: boolean;
  session: SessionData | null;
  login: (data: SessionData) => void;
  logout: () => void;
  refreshSession: () => Promise<SessionData | null>;
}

const SESSION_KEY = "user_session";

const persistSession = (data: SessionData) => {
  if (typeof window === "undefined") return;

  const preferredStorage = data.rememberMe ? localStorage : sessionStorage;
  const fallbackStorage = data.rememberMe ? sessionStorage : localStorage;

  preferredStorage.setItem(SESSION_KEY, JSON.stringify(data));
  fallbackStorage.removeItem(SESSION_KEY);
};

export const getStoredSession = (): SessionData | null => {
  if (typeof window === "undefined") return null;

  const storagePriority = [localStorage, sessionStorage];

  for (const storage of storagePriority) {
    try {
      const rawSession = storage.getItem(SESSION_KEY);
      if (rawSession) {
        return JSON.parse(rawSession) as SessionData;
      }
    } catch (error) {
      // Ignore malformed session data and continue checking the next storage.
    }
  }

  return null;
};

const decodeJwtPayload = (token?: string): Record<string, any> | null => {
  if (!token || typeof token !== "string") return null;

  const parts = token.split(".");
  if (parts.length !== 3) return null;

  try {
    const base64Payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64Payload.padEnd(
      base64Payload.length + ((4 - (base64Payload.length % 4)) % 4),
      "=",
    );
    const decoded = atob(padded);
    const json = decodeURIComponent(
      decoded
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
};

const isJwtExpired = (token?: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return Date.now() >= payload.exp * 1000;
};

const refreshTokenCodec = t.type({
  userId: t.union([t.string, t.undefined]),
  email: t.union([t.string, t.undefined]),
  roleInFirm: t.union([t.string, t.undefined, t.null]),
  accessToken: t.string,
  refreshToken: t.string,
});

export const refreshUserSession = async (
  refreshToken?: string,
  rememberMe: boolean = false,
): Promise<SessionData | null> => {
  if (!refreshToken) return null;

  try {
    const data = await fetchApi({
      service: "user-service",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      pathname: "/refresh-token",
      codec: refreshTokenCodec,
      body: { refreshToken },
    });

    const accessToken = data?.accessToken;
    const nextRefreshToken = data?.refreshToken;
    const payload = accessToken ? decodeJwtPayload(accessToken) : null;

    if (!accessToken || !nextRefreshToken) {
      return null;
    }

    const nextSession: SessionData = {
      token: accessToken,
      refreshToken: nextRefreshToken,
      userId: data?.userId || payload?.userId || payload?.id || "",
      username: payload?.username || data?.username || "",
      email: payload?.email || data?.email || "",
      name: payload?.name || data?.name || "",
      role: (data?.roleInFirm as RoleT | undefined) || undefined,
      isAuthenticated: true,
      expiresAt: payload?.exp ? payload.exp * 1000 : undefined,
      rememberMe,
    };

    persistSession(nextSession);
    return nextSession;
  } catch (error) {
    return null;
  }
};

function useSession(): UseSession {
  const [session, setSessionState] = useState<SessionData | null>(null);
  const [authState] = useAtom(authStore);
  const { isAuthenticated } = authState;
  const isRefreshingRef = useRef(false);

  const logoutHandler = () => {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    setSessionState(null);
    logout();
  };

  const loginHandler = (data: SessionData) => {
    const payload = decodeJwtPayload(data.token);
    const nextSession: SessionData = {
      ...data,
      rememberMe: data.rememberMe ?? false,
      isAuthenticated: true,
      expiresAt:
        data.expiresAt ||
        (payload && typeof payload.exp === "number"
          ? payload.exp * 1000
          : undefined),
    };

    persistSession(nextSession);
    setSessionState(nextSession);
    login();
  };

  const refreshSessionHandler = useCallback(async () => {
    const storedSession = getStoredSession();
    if (!storedSession) {
      logoutHandler();
      return null;
    }

    if (!storedSession.refreshToken) {
      return null;
    }

    if (isRefreshingRef.current) {
      return null;
    }

    isRefreshingRef.current = true;

    try {
      const refreshedSession = await refreshUserSession(
        storedSession.refreshToken,
        storedSession.rememberMe ?? false,
      );

      if (!refreshedSession) {
        logoutHandler();
        return null;
      }

      setSessionState(refreshedSession);
      login();
      return refreshedSession;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  useEffect(() => {
    const storedSession = getStoredSession();
    if (!storedSession) {
      setSessionState(null);
      return;
    }

    const normalizedSession = {
      ...storedSession,
      isAuthenticated:
        isAuthenticated || storedSession.isAuthenticated === true,
    };

    setSessionState(normalizedSession);

    if (normalizedSession.token && isJwtExpired(normalizedSession.token)) {
      if (isRefreshingRef.current) return;
      refreshSessionHandler();
    }
  }, [isAuthenticated, refreshSessionHandler]);

  return {
    session,
    isAuthenticated,
    login: (data: SessionData) => loginHandler(data),
    logout: logoutHandler,
    refreshSession: refreshSessionHandler,
  };
}

export default useSession;
